/**
 * What session replays (PostHog and Sentry) must not show as readable text: post bodies,
 * comments and chat/DM messages. Members' words, not the UI around them, so replays stay useful
 * for debugging. Typed inputs are masked separately (maskAllInputs).
 *
 * - `.ph-mask`              anything marked by hand (feed comments)
 * - `.post-body`            a feed post's body (fe-shared-lib pages/feed *UI.tsx)
 * - `.post-message__text`   a chat message (Mattermost post_message_view)
 * - `.post__body`           a chat post body, incl. attachments' text (Mattermost post_body)
 * - `.post-preview`         a quoted/permalinked chat message (Mattermost post_message_preview)
 */
export const REPLAY_MASK_SELECTORS = ['.ph-mask', '.post-body', '.post-message__text', '.post__body', '.post-preview'];

export const REPLAY_MASK_SELECTOR = REPLAY_MASK_SELECTORS.join(', ');

/**
 * Hide by default (decision D9, 2026-10-28). PostHog replays mask EVERY text node, and reveal only
 * what sits inside a container marked `ph-unmask`: the header, the sidebar navigation, the admin
 * rail and top bar. A new screen therefore leaks nothing until someone marks it safe, and a mistake
 * hides too much rather than too little. Member data inside a safe container stays hidden when it
 * is wrapped in `ph-mask` (the notification and message dropdowns in the header) or is a table body.
 */
export const REPLAY_UNMASK_CLASS = 'ph-unmask';
export const REPLAY_UNMASK_SELECTOR = `.${REPLAY_UNMASK_CLASS}`;
/** Media never reaches a recording: avatars, post photos, banners, logos, video, canvas. */
export const REPLAY_BLOCK_SELECTOR = 'img, picture, video, canvas';
/** Inside a safe container, these still hide: hand-marked member data and any table body. */
const REPLAY_KEEP_MASKED_SELECTOR = `${REPLAY_MASK_SELECTOR}, tbody, [role="grid"], [role="rowgroup"]`;

/** posthog-js `maskTextFn`: the text itself only inside a safe container, masked everywhere else. */
export function maskReplayText(text: string, element: HTMLElement | null): string {
	try {
		if (element && typeof element.closest === 'function') {
			if (element.closest(REPLAY_KEEP_MASKED_SELECTOR)) return '*'.repeat(text.length);
			if (element.closest(REPLAY_UNMASK_SELECTOR)) return text;
		}
	} catch {
		// an odd node; mask it
	}
	return '*'.repeat(text.length);
}
